import { createClient } from '@supabase/supabase-js';

// Define a type for our real-time payload with proper typing
export type RealtimePayload = {
  commit_timestamp: string;
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
  schema: string;
  table: string;
  new: Record<string, any> | null;
  old: Record<string, any> | null;
}

// Keep track of processed IDs to prevent duplicates
const processedItems = new Map<string, number>();
// Track processed real-time events to prevent duplication
const processedEvents = new Map<string, number>();
// Track transaction submissions to prevent duplicates
const submittedTransactions = new Map<string, number>();

// URL e chave pública do Supabase - estas são seguras para expor no frontend
// pois são limitadas pelo RLS (Row Level Security)
// URLs públicas podem ser expostas sem problemas
const SUPABASE_URL = 'https://zivnpumqyyflyxkecmdz.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inppdm5wdW1xeXlmbHl4a2VjbWR6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Mzc5MzY3NDUsImV4cCI6MjA1MzUxMjc0NX0.sFI49KdTSr5PlN--Q9N1eER6mpAvDaIYzYzjhjGdf_w';

// Create Supabase client with improved configuration
export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    storage: localStorage,
    storageKey: 'supabase_auth_token',
    detectSessionInUrl: false
  },
  realtime: {
    params: {
      eventsPerSecond: 2 // Increased limit for better responsiveness
    }
  },
  global: {
    fetch: (...args) => {
      // Improved error handling and retry logic for network requests
      const fetchWithRetry = async (attempt = 1, maxAttempts = 3) => {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 15000); // 15s timeout
          
          // Properly handle args to fix TypeScript spread operator error
          const [url, options = {}] = args;
          const response = await fetch(url, { 
            ...options,
            signal: controller.signal
          });
          
          clearTimeout(timeoutId);
          return response;
        } catch (error) {
          if (attempt < maxAttempts) {
            console.log(`Retry attempt ${attempt} after fetch error:`, error);
            // Exponential backoff with jitter for more resilient retries
            const backoff = Math.min(1000 * Math.pow(2, attempt - 1), 10000);
            const jitter = Math.random() * 1000;
            await new Promise(r => setTimeout(r, backoff + jitter));
            return fetchWithRetry(attempt + 1, maxAttempts);
          }
          throw error;
        }
      };
      
      return fetchWithRetry();
    }
  }
});

// Prevent duplicate real-time callbacks with this flag
let isProcessing = {
  transaction: false,
  quotation: false
};
let lastApiCallTime = 0;
const API_THROTTLE_TIME = 1000; // 1 second

// Function to create a hash from transaction data for comparison - Melhorado
const createDataHash = (data: any): string => {
  // Create a stable representation by sorting keys
  const stableData = {
    type: data.type || '',
    customer_id: data.customer_id || '',
    amount: Number(data.amount || 0).toFixed(2), // Normalize to 2 decimal places
    name: (data.name || '').trim().toLowerCase(), // Normalize case
    description: (data.description || '').trim().toLowerCase(), // Normalize case
    payment_method: (data.payment_method || '').toLowerCase(), // Normalize case
    payment_status: (data.payment_status || '').toLowerCase(), // Normalize case
    category: (data.category || '').toLowerCase(), // Normalize case
    due_date: data.due_date || '',
    // Use 5-minute window for duplicate detection
    timeWindow: Math.floor(Date.now() / (1000 * 60 * 5)), // 5-minute window
  };
    
  // Create a stable JSON string
  const sortedKeys = Object.keys(stableData).sort();
  const sortedObject = {};
  
  for (const key of sortedKeys) {
    if (stableData[key] !== null && stableData[key] !== undefined && stableData[key] !== '') {
      sortedObject[key] = stableData[key];
    }
  }
  
  return JSON.stringify(sortedObject);
};

// Define a type for the Supabase responses for consistency
export type DatabaseResponse<T = any> = {
  data: T | null;
  error: Error | null;
};

// Improved transaction fetching function with better error handling and throttling
export const fetchTransactionsWithRetry = async (filters?: Record<string, any>): Promise<DatabaseResponse> => {
  // Implement API call throttling to prevent excessive requests
  const now = Date.now();
  if (now - lastApiCallTime < API_THROTTLE_TIME) {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve(fetchTransactionsWithRetry(filters));
      }, API_THROTTLE_TIME - (now - lastApiCallTime));
    });
  }
  lastApiCallTime = now;
  
  let retries = 0;
  const maxRetries = 3;
  
  while (retries < maxRetries) {
    try {
      let query = supabase
        .from('transactions')
        .select(`
          *,
          customers (
            name,
            phone
          )
        `)
        .order('created_at', { ascending: false });
      
      // Apply filters if provided
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value) {
            if (key === 'startDate') {
              query = query.gte('created_at', value);
            } else if (key === 'endDate') {
              const nextDay = new Date(value as string);
              nextDay.setDate(nextDay.getDate() + 1);
              const formattedNextDay = nextDay.toISOString().split('T')[0];
              query = query.lt('created_at', formattedNextDay);
            } else {
              query = query.eq(key, value);
            }
          }
        });
      }
      
      const { data, error } = await query.limit(100);
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      retries++;
      if (retries >= maxRetries) {
        console.error('Failed to fetch transactions after multiple attempts:', error);
        return { data: null, error: error as Error };
      }
      
      // Wait before retrying with exponential backoff
      await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retries)));
    }
  }
  
  return { data: null, error: new Error('Failed to fetch transactions after multiple attempts') };
};

// Function to check for duplicate transactions before insertion - Melhorado
export const checkDuplicateTransaction = async (transactionData: any): Promise<boolean> => {
  try {
    // Create a hash of this transaction for local cache checking
    const transactionHash = createDataHash(transactionData);
    
    // Check if this transaction has been submitted recently (within 2 minutos)
    const now = Date.now();
    const recentSubmission = submittedTransactions.get(transactionHash);
    if (recentSubmission && now - recentSubmission < 120000) { // 2 minutos
      console.log('Found duplicate in local submission cache:', transactionHash);
      return true;
    }
    
    // Store this submission to prevent duplicates
    submittedTransactions.set(transactionHash, now);
    
    // Clean up old submissions after 5 minutes
    setTimeout(() => {
      submittedTransactions.delete(transactionHash);
    }, 300000); // 5 minutes
    
    // First, check if there's an exact match created very recently (within 5 minutes)
    const fiveMinutesAgo = new Date();
    fiveMinutesAgo.setMinutes(fiveMinutesAgo.getMinutes() - 5);
    
    let query = supabase
      .from('transactions')
      .select('id, created_at, amount, type')
      .eq('type', transactionData.type)
      .eq('amount', transactionData.amount)
      .gte('created_at', fiveMinutesAgo.toISOString());
    
    // Add customer_id condition only if it exists
    if (transactionData.customer_id) {
      query = query.eq('customer_id', transactionData.customer_id);
    } else {
      query = query.is('customer_id', null);
    }
    
    // Add additional filtering for non-empty fields
    if (transactionData.name) {
      query = query.eq('name', transactionData.name);
    }
    
    if (transactionData.category) {
      query = query.eq('category', transactionData.category);
    }
    
    if (transactionData.description) {
      query = query.ilike('description', `%${transactionData.description}%`);
    }
    
    const { data } = await query;
      
    // If we find any matching transactions, consider it a duplicate
    if (data && data.length > 0) {
      console.log('Found duplicate transaction in database:', data);
      return true;
    }
    
    return false; // No duplicates found
  } catch (error) {
    console.error('Error checking for duplicate transactions:', error);
    return false; // If check fails, allow transaction to proceed
  }
};

// Function to add a transaction with safeguards against duplicate inserts
export const addTransaction = async (transactionData: any): Promise<DatabaseResponse> => {
  // First check if this might be a duplicate  
  const isDuplicate = await checkDuplicateTransaction(transactionData);
  if (isDuplicate) {
    console.log('Preventing duplicate transaction submission');
    return { 
      data: null, 
      error: new Error("Uma transação similar já foi registrada recentemente.") 
    };
  }
  
  try {
    // Set the processing flag
    isProcessing.transaction = true;
    
    // Insert the transaction
    const { data, error } = await supabase.from('transactions').insert(transactionData).select();
    
    // If successful, store the transaction ID to prevent duplicates from real-time events
    if (!error && data && data.length > 0) {
      const transactionId = data[0]?.id;
      if (transactionId) {
        processedItems.set(`transaction-${transactionId}`, Date.now());
        
        // Clean up old transaction IDs after 10 seconds to prevent memory leaks
        setTimeout(() => {
          processedItems.delete(`transaction-${transactionId}`);
        }, 10000);
      }
    }
    
    return { data, error };
  } catch (error) {
    return { data: null, error: error as Error };
  } finally {
    // Release the lock after a delay
    setTimeout(() => {
      isProcessing.transaction = false;
    }, 2000);
  }
};

// Function to check if an item has already been processed
export const hasProcessedItem = (type: string, itemId: string): boolean => {
  const key = `${type}-${itemId}`;
  return processedItems.has(key);
};

// Helper to safely get an ID from a realtime payload
export const getPayloadId = (payload: RealtimePayload): string | undefined => {
  // Try to get ID from new record, then old record
  return payload.new?.id || payload.old?.id;
};

// Helper to safely get a created_by value from a realtime payload
export const getPayloadCreatedBy = (payload: RealtimePayload): string | undefined => {
  return payload.new?.created_by || payload.old?.created_by;
};

// Function to track and deduplicate real-time events - improved version
export const trackEvent = (eventType: string, entityId: string): boolean => {
  const eventKey = `${eventType}-${entityId}`;
  const now = Date.now();
  
  // Check if we've seen this event recently (within 5 seconds)
  const lastSeen = processedEvents.get(eventKey);
  if (lastSeen && (now - lastSeen) < 5000) {
    console.log(`Skipping duplicate event: ${eventKey} (seen ${now - lastSeen}ms ago)`);
    return true; // Already processed
  }
  
  // Update the last seen timestamp
  processedEvents.set(eventKey, now);
  
  // Clean up old events to prevent memory leaks (after 1 minute)
  setTimeout(() => {
    processedEvents.delete(eventKey);
  }, 60000);
  
  return false; // New event
};

// Função para gerenciar sessões do usuário através da Edge Function
export const manageUserSession = async (userId: string, sessionId: string) => {
  try {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/auth`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({
        action: 'manage_session',
        user_id: userId,
        session_id: sessionId
      })
    });

    if (!response.ok) {
      throw new Error(`Erro HTTP: ${response.status}`);
    }

    const result = await response.json();
    return result.success;
  } catch (error) {
    console.error('Erro ao gerenciar sessão:', error);
    return false;
  }
};
